import React from 'react';
import { type Clinician, useClinicians } from '@features/organizations';
import { ProviderBadge } from '@jotpsych/ui';
import { Autocomplete, Box, TextField } from '@mui/material';

interface ProviderPickerProps {
  value?: string;
  onChange: (providerId: string) => void;
}

export const ProviderPicker: React.FC<ProviderPickerProps> = ({ value, onChange }) => {
  const { clinicians, isLoading } = useClinicians();

  const selectedClinician = clinicians.find(c => c.userId === value) ?? null;

  return (
    <Autocomplete<Clinician>
      options={clinicians}
      loading={isLoading}
      value={selectedClinician}
      onChange={(_event, newValue) => {
        if (newValue) {
          onChange(newValue.userId);
        }
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.userId === val.userId}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <Box
            component="li"
            key={key}
            {...optionProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ProviderBadge name={option.name} />
            <Box
              component="span"
              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {option.name}
            </Box>
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label="Provider"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: selectedClinician ? (
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5, mr: 0.5 }}>
                  <ProviderBadge name={selectedClinician.name} />
                </Box>
              ) : (
                params.slotProps.input.startAdornment
              ),
            },
          }}
        />
      )}
    />
  );
};

export default ProviderPicker;
